import { Button, Checkbox, Icon } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import {
  companyContentMoveService,
  type CompanyContentMoveApplyResponse,
  type CompanyContentMoveConflict,
  type CompanyContentMoveOperation,
  type CompanyContentMoveValidateResponse,
} from "@/api/profiles/companyContentMoveService";
import styles from "./UndoMoveModal.module.css";
import { TABLE_LABELS, downloadManifest, nonZeroCounts } from "./shared";

function conflictSlot(conflict: CompanyContentMoveConflict): string {
  if (conflict.kind === "duplicate_page_type_website") {
    return `page type ${conflict.pageType} / website ${conflict.website}`;
  }
  return `language ${conflict.languageCode ?? "(none)"} / website ${conflict.website}`;
}

export type UndoMoveModalProps = {
  /** The operation being undone. Mounting the modal opens it. */
  operation: CompanyContentMoveOperation;
  /** Best-effort resolved name of the operation's ORIGINAL source (where rows return to); null falls back to the uuid. */
  sourceName: string | null;
  /** Best-effort resolved name of the operation's ORIGINAL target (where rows are now); null falls back to the uuid. */
  targetName: string | null;
  onClose: () => void;
  /** Fired once when the undo apply succeeds, so the page can refetch after the modal closes. */
  onUndone?: () => void;
};

/**
 * SOFTSUPP-530 undo, via the server-side by-operation-id endpoints
 * (`operations/{moveId}/undo/validate` + `operations/{moveId}/undo`): the
 * server loads the stored operation, swaps source and target, restricts the
 * reverse move to the operation's own `movedIds` manifest — so a row moved
 * somewhere else since the original operation no longer matches and stays
 * put — and stamps `undoesMoveId` linking back to the undone operation. Those
 * semantics are unchanged from the old client-side flow (swap + flattened
 * `onlyRowIds`), just computed server-side, so the undo works for operations
 * of ANY size — the client-side flatten was capped at 5000 ids by the request
 * schema. Opens straight into the dry run.
 *
 * Conflicts (the reverse move landing on a slot re-occupied since) are shown
 * as a flat, always-skipped list — no per-row decision UI here, the safe
 * default is the only offered option and their sourceRowIds go out as
 * `skipRowIds`.
 */
export function UndoMoveModal({
  operation,
  sourceName,
  targetName,
  onClose,
  onUndone,
}: UndoMoveModalProps) {
  const [validation, setValidation] = useState<CompanyContentMoveValidateResponse | null>(null);
  const [applied, setApplied] = useState<CompanyContentMoveApplyResponse | null>(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState<"validate" | "apply" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;

  // The dry run of the EXACT reverse request — source/target swap and the
  // manifest restriction are derived server-side from the stored operation.
  useEffect(() => {
    let active = true;
    setBusy("validate");
    setError(null);
    companyContentMoveService
      .undoValidate(operation.id)
      .then(result => {
        if (!active) return;
        setValidation(result);
        setBusy(null);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Validation failed");
        setBusy(null);
      });
    return () => {
      active = false;
    };
  }, [operation]);

  // ESC closes, but never while a request is in flight (busy via ref so the
  // listener does not rebind mid-request).
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && busyRef.current === null) {
        onClose();
      }
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Focus trap: focus the first focusable on open, cycle Tab within the
  // dialog, restore focus to the opener on close.
  useEffect(() => {
    const previouslyFocusedElement = document.activeElement as HTMLElement;
    const modal = modalRef.current;
    if (modal) {
      const focusable = modal.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) {
        (focusable as HTMLElement).focus();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Tab" && modal) {
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    modal?.addEventListener("keydown", handleKeyDown);
    return () => {
      modal?.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus();
    };
  }, []);

  const returnToLabel = sourceName ?? operation.sourceCompanyId;
  const currentlyAtLabel = targetName ?? operation.targetCompanyId;

  const conflictSkipIds = validation ? validation.conflicts.map(c => c.sourceRowId) : [];
  const willUndo = validation ? validation.totalToMove - conflictSkipIds.length : 0;
  const nothingToUndo = validation !== null && validation.totalToMove === 0;
  const confirmDisabled = busy !== null || validation === null || willUndo <= 0 || !ack;

  async function handleConfirm() {
    if (validation === null) return;
    setBusy("apply");
    setError(null);
    try {
      const result = await companyContentMoveService.undo(
        operation.id,
        conflictSkipIds.length > 0 ? { skipRowIds: conflictSkipIds } : {},
      );
      setApplied(result);
      onUndone?.();
    } catch (err) {
      // Stay on the review with the dry run intact — the operator reads the
      // error and retries (or bails out and investigates).
      setError(err instanceof Error ? err.message : "Undo failed");
    } finally {
      setBusy(null);
    }
  }

  function handleDownloadManifest() {
    if (!applied) return;
    // The NEW manifest — the undo is itself a move with its own rollback record.
    downloadManifest({
      sourceCompanyId: operation.targetCompanyId,
      targetCompanyId: operation.sourceCompanyId,
      movedIds: applied.movedIds,
      totalMoved: applied.totalMoved,
      appliedAt: new Date().toISOString(),
    });
  }

  function handleBackdropClick(event: React.MouseEvent) {
    if (event.target === event.currentTarget && busy === null) {
      onClose();
    }
  }

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="undo-move-title"
      >
        <div className={styles.modalHeader}>
          <h2 id="undo-move-title" className={styles.modalTitle}>
            Undo content move
          </h2>
          <button
            type="button"
            className={styles.modalCloseButton}
            onClick={onClose}
            disabled={busy !== null}
            aria-label="Close modal"
          >
            <Icon icon="cross" size="small" />
          </button>
        </div>

        <div className={styles.modalBody}>
          {applied === null && (
            <>
              <p className={styles.intro}>
                Re-points the rows this operation moved from <strong>{currentlyAtLabel}</strong>{" "}
                back to <strong>{returnToLabel}</strong>, restricted to the operation's own
                manifest. A row moved elsewhere since the original operation no longer matches the
                manifest and stays put.
              </p>

              {busy === "validate" && (
                <p className={styles.loadingText} aria-live="polite">
                  Running the dry run…
                </p>
              )}

              {validation && (
                <>
                  {nothingToUndo ? (
                    <p className={styles.emptyText}>
                      There is nothing left to undo — none of the moved rows are still at{" "}
                      {currentlyAtLabel}.
                    </p>
                  ) : (
                    <>
                      <div className={styles.summaryBox}>
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>Rows to return:</span>
                          <span className={styles.infoValue}>
                            {willUndo} of {operation.totalMoved} originally moved
                            {conflictSkipIds.length > 0 &&
                              ` (${conflictSkipIds.length} skipped as conflicts)`}
                          </span>
                        </div>
                      </div>

                      <ul className={styles.countsList} aria-label="Rows to return per table">
                        {nonZeroCounts(validation.counts).map(([key, count]) => (
                          <li key={key} className={styles.countsItem}>
                            <span>{TABLE_LABELS[key]}</span>
                            <span className={styles.countBadge}>{count}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {validation.conflicts.length > 0 && (
                    <section aria-label="Conflicts">
                      <h3 className={styles.sectionHeading}>
                        Conflicts ({validation.conflicts.length}) — will be skipped
                      </h3>
                      <p className={styles.sectionNote}>
                        {returnToLabel} has re-occupied these slots since the original move.
                        Returning these rows would leave two live rows in the same slot — nothing in
                        the database prevents that — so they are skipped and stay at{" "}
                        {currentlyAtLabel}.
                      </p>
                      <ul className={styles.conflictList}>
                        {validation.conflicts.map(conflict => (
                          <li key={conflict.sourceRowId} className={styles.conflictRow}>
                            <span className={styles.conflictTable}>
                              {TABLE_LABELS[conflict.table]}
                            </span>
                            <span className={styles.conflictSlot}>{conflictSlot(conflict)}</span>
                            <code className={styles.uuid}>{conflict.sourceRowId}</code>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {validation.warnings.length > 0 && (
                    <p className={styles.sectionNote}>
                      {validation.warnings.length} duplicate-contact warning
                      {validation.warnings.length === 1 ? "" : "s"}: {returnToLabel} already has a
                      lawyer profile for the same contact. Duplicates are allowed, so these rows
                      still return.
                    </p>
                  )}

                  {!nothingToUndo && willUndo > 0 && (
                    <Checkbox
                      label={`I understand this re-points ${willUndo} rows back to ${returnToLabel}`}
                      // The design-system Checkbox's `label` prop is a bare span,
                      // not linked to the input — the accessible name needs this.
                      aria-label={`I understand this re-points ${willUndo} rows back to ${returnToLabel}`}
                      checked={ack}
                      disabled={busy !== null}
                      onChange={event => setAck(event.target.checked)}
                    />
                  )}
                </>
              )}
            </>
          )}

          {applied && (
            <>
              <p className={styles.resultSummary}>
                Returned <strong>{applied.totalMoved}</strong> rows to{" "}
                <strong>{returnToLabel}</strong>.
              </p>

              {applied.totalMoved > 0 && (
                <ul className={styles.countsList} aria-label="Rows returned per table">
                  {nonZeroCounts(applied.counts).map(([key, count]) => (
                    <li key={key} className={styles.countsItem}>
                      <span>{TABLE_LABELS[key]}</span>
                      <span className={styles.countBadge}>{count}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className={styles.manifestBox}>
                <p className={styles.manifestNote}>
                  The undo is itself a recorded move with its own <strong>rollback manifest</strong>{" "}
                  — download it if you may need to reverse the undo.
                </p>
                <Button variant="outlined" color="default" onClick={handleDownloadManifest}>
                  Download manifest
                </Button>
              </div>
            </>
          )}

          {error && (
            <p className={styles.errorText} role="alert">
              {error}
            </p>
          )}
        </div>

        <div className={styles.modalFooter}>
          {applied === null ? (
            <>
              <Button variant="outlined" color="default" onClick={onClose} disabled={busy !== null}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={confirmDisabled}
                onClick={() => void handleConfirm()}
              >
                {busy === "apply" ? "Undoing…" : "Undo move"}
              </Button>
            </>
          ) : (
            <Button variant="contained" color="primary" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default UndoMoveModal;
